import {
  BaseUnits,
  CoordinateType,
  DeviceMode,
  ProductType,
  SensorType,
} from '../common/enums'

export enum ConversionMethod {
  None = 'None',
  Method1 = 'Method1', // [y = (x * scale) + offset]
  Method2 = 'Method2', // [y = (x + offset) * scale]
  Method3 = 'Method3', // [y = (x / scale) + offset]
  Method4 = 'Method4', // [y = (x + offset) / scale]
}

interface Location {
  // name: string
  // code: string
  description: string
  // latitude: number
  // longitude: number
  coordinateType: CoordinateType
  coord: {
    ns: number // north-south
    ew: number // east-west
    z: number
    gridZone: string
  }
}

// Considerations:
// use percentages instead of numbers
// trigger based on delta from previous reading
export interface SensorThresholdSettings {
  configured: boolean
  low: {
    value: number
    enabled: boolean
    triggered: boolean
  }
  high: {
    value: number
    enabled: boolean
    triggered: boolean
  }
  delta: {
    value: number
    enabled: boolean
    triggered: boolean
  }
}

interface SensorConversionSettings {
  method: ConversionMethod
  scale: number
  offset: number
}

export interface SensorConfig {
  sensorType: SensorType
  units: BaseUnits
  enabled: boolean
  thresholds: SensorThresholdSettings
  conversion: SensorConversionSettings
}

export type Sensors = { [key: string]: SensorConfig }

interface DeviceConfig {
  firmwareVersion: number

  site: {
    name: string
    code: string
  }

  frequency: {
    snts: number
    schd: number
  }

  device: {
    name: string
    mode: DeviceMode
    pin: string
    configId: number
    configFlags: number
  }

  sensors: Sensors
  location: Location
}

interface DeviceInfo {
  deviceId: number
  wisolId: string
  wisolPac: string
  serialNumber: number
  hardwareVersion: number
  sigfoxReg: number
  productType: ProductType
  dateCode: string
  deviceKey: string
  messageSntsId: number
  runId: number // just store the run directly on the device
}

interface DeviceState {
  readings: {
    battery: number
    temperature: number
    sensors?: number[]
  }
  alerts: {
    batteryLowShutdown: boolean
    batteryLowWarning: boolean
  }
  flags: {
    enableSntsBatteryReading: boolean
    enableSchdTemperatureReading: boolean
    inConfigMode: boolean
    inScheduledStartMode: boolean
  }
  test: {
    message: string
  }
}

export interface DeviceTracking {
  last: {
    rawMessageId: number
    update: number
  }
  configUpdate: {
    active: boolean
    lastUpdate: number
    configured: boolean
  }
  timestamps: {
    testMessage: number
    sntsMessage: number
    schdMessage: number
    deviceConfig1: number
    deviceConfig2: number
    deviceConfig3: number
    deviceConfig4: number
    deviceConfig5: number
    sensorConfig1: number
  }
}

// export interface DeviceMetadata {
//   tracking: {
//     last: {
//       rawMessageId: number
//       update: number
//     }
//     configUpdate: {
//       active: boolean
//       lastUpdate: number
//       configured: boolean
//     }
//     timestamps: {
//       testMessage: number
//       sntsMessage: number
//       schdMessage: number
//       deviceConfig1: number
//       deviceConfig2: number
//       deviceConfig3: number
//       deviceConfig4: number
//       deviceConfig5: number
//       sensorConfig1: number
//     }
//   }
// }

export interface DeviceData {
  id: string
  sigfoxId: string
  deviceId: number
  info: DeviceInfo
  config: DeviceConfig
  state: DeviceState
  tracking: DeviceTracking
  readings: SensorReadings
}

// timestamp --> A1, A2, B1, B2, C1, C2, C3, Battery, Temperature
// timestamp
// reading type
// value
// units

// charts --> display for single RIC (but only display charts for things that exist)
// A1
// A2
// B1
// B2
// C1
// C2
// C3
// battery
// temperature
// messages

// export type DeviceReadings = { [key: string]: { [key: string]: DeviceReading } }
export type SensorReadings = { [key: string]: SensorReadingData }

// export type Reading = [number, number | string]
export type Reading = [number, number | string]

export interface SensorReadingData {
  metadata: ReadingsMetadata
  data: { [key: number]: number | string }
}

export interface ReadingsMetadata {
  sensorType: SensorType | string
  units: BaseUnits
  lastReadingTimestamp: number
  lastRawMessageId: number
  latestOverallReadingTimestamp: number
}
